import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import Header from 'components/Header'
import StudentSearchBar from 'components/StudentSearch'
import StaffSearch from 'components/StaffSearch'
import { useBasePath } from 'components/Providers'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getAllStudents } from 'store/lookup'
import {
  clearPickupList,
  getPickupList,
  save,
  search,
} from 'store/visitor-log'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const purposeOptions = [
  'Meeting',
  'Delivery',
  'Maintenance',
  'Other',
]

const visitorTypeOptions = [
  { value: 'parent', label: 'Parent' },
  { value: 'staff', label: 'Staff' },
  { value: 'external', label: 'External' },
]

export default function VisitorLogForm() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()

  const allStudentsState = useSelector(state => state.lookup.allStudents)
  const pickupList = useSelector(state => state.visitorLog.pickupList)

  const allStudents =
    allStudentsState?.rows ||
    allStudentsState?.list ||
    []

  const [visitType, setVisitType] = useState('visit')
  const [visitorType, setVisitorType] = useState('')
  const [fullName, setFullName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [selectedPickup, setSelectedPickup] = useState(null)
  const [badgeNumber, setBadgeNumber] = useState('')
  const [saving, setSaving] = useState(false)

  const selectedStudentId = selectedStudent?.personId || selectedStudent?.id

  useEffect(() => {
    if (!allStudents.length) {
      dispatch(getAllStudents({ limit: 1000 }))
    }

    return () => {
      dispatch(clearPickupList())
    }
  }, [dispatch])

  useEffect(() => {
    setSelectedPickup(null)

    if (selectedStudentId) {
      dispatch(
        getPickupList({
          studentId: selectedStudentId,
        }),
      )
    } else {
      dispatch(clearPickupList())
    }
  }, [dispatch, selectedStudentId])

  const handleVisitTypeChange = event => {
    const value = event.target.value

    setVisitType(value)
    setFullName('')
    setPurpose('')
    setSelectedStaff(null)
    setSelectedStudent(null)
    setSelectedPickup(null)
    setBadgeNumber('')
  }

  const splitFullName = name => {
    const parts = name.trim().split(' ')
    const firstName = parts.shift() || ''
    const lastName = parts.join(' ')

    return {
      firstName,
      lastName,
    }
  }

  const handleSave = async () => {
    let payload

    if (visitType === 'pickup') {
      if (!selectedStudent || !selectedPickup) return
      if (selectedPickup.pickup !== true) return

      payload = {
        visitType: 'pickup',
        visitorType: selectedPickup.contact?.type || 'parent',
        visitorFirstName: selectedPickup.contact?.firstName || '',
        visitorLastName: selectedPickup.contact?.lastName || '',
        visitorPhone: null,
        personId: selectedStudentId,
        photoId: null,
        note: purpose || 'Student Pickup',
        badgeNumber: null,
      }
    } else {
      if (!fullName || !purpose || !selectedStaff || !badgeNumber) {
        return
      }

      const name = splitFullName(fullName)

      payload = {
        visitType: 'visit',
        visitorType,
        visitorFirstName: name.firstName,
        visitorLastName: name.lastName,
        visitorPhone: null,
        personId: selectedStaff.personId || selectedStaff.id,
        photoId: null,
        note: purpose,
        badgeNumber: Number(badgeNumber),
      }
    }

    setSaving(true)

    const res = await dispatch(save(payload))

    setSaving(false)

    if (res.payload) {
      dispatch(
        search({
          date: dayjs().format('YYYY-MM-DD'),
        }),
      )

      navigate(basePath)
    }
  }

  const pickupIsNotPermitted =
    selectedPickup && selectedPickup.pickup !== true

  const saveDisabled =
    saving ||
    (
      visitType === 'visit'
        ? !fullName ||
        !purpose ||
        !selectedStaff ||
        !badgeNumber
        : !selectedStudent ||
        !selectedPickup ||
        pickupIsNotPermitted
    )

  return (
    <Card
      elevation={0}
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header
        title={t('New Check-In')}
        small
        close
        borderBottom
        onClose={() => navigate(basePath)}
      />

      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {t('Visitor Type')}
            </Typography>

            <RadioGroup
              row
              value={visitType}
              onChange={handleVisitTypeChange}
            >
              <FormControlLabel
                value="visit"
                control={<Radio size="small" />}
                label={t('General Visitor')}
              />

              <FormControlLabel
                value="pickup"
                control={<Radio size="small" />}
                label={t('Student Pickup')}
              />
            </RadioGroup>
          </Box>

          {visitType === 'visit' && (
            <>
              <TextField
                select
                fullWidth
                size="small"
                required
                label={t('Visitor Type')}
                placeholder={t('Select Visitor Type')}
                value={visitorType}
                onChange={event => setVisitorType(event.target.value)}
              >
                {visitorTypeOptions.map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    {t(item.label)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                size="small"
                required
                label={t('Visitor Name')}
                placeholder={t('Enter full name')}
                value={fullName}
                onChange={event => setFullName(event.target.value)}
              />

              <TextField
                select
                fullWidth
                size="small"
                required
                label={t('Reason For Visit')}
                value={purpose}
                onChange={event => setPurpose(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: value =>
                    value ? t(value) : t('Select reason'),
                }}
              >
                {purposeOptions.map(item => (
                  <MenuItem key={item} value={item}>
                    {t(item)}
                  </MenuItem>
                ))}
              </TextField>

              <StaffSearch
                label={t('Visiting/Assigned Staff')}
                value={selectedStaff}
                onSelect={setSelectedStaff}
              />

              <TextField
                fullWidth
                size="small"
                required
                type="number"
                label={t('Pass Number')}
                placeholder={t('Enter pass number')}
                value={badgeNumber}
                onChange={event => setBadgeNumber(event.target.value)}
                inputProps={{
                  min: 1,
                  max: 10,
                }}
              />
            </>
          )}

          {visitType === 'pickup' && (
            <>
              <StudentSearchBar
                value={selectedStudent}
                onSelect={value => setSelectedStudent(value)}
              />

              <Autocomplete
                fullWidth
                size="small"
                options={pickupList || []}
                value={selectedPickup}
                disabled={!selectedStudent}
                onChange={(event, value) => setSelectedPickup(value)}
                getOptionLabel={option => {
                  const contact = option.contact || {}

                  const name =
                    `${contact.firstName || ''} ${contact.lastName || ''}`.trim()

                  const relationship = option.relationship || ''

                  return option.pickup
                    ? `${name} - ${relationship}`
                    : `${name} - ${relationship} - ${t('Not Permitted')}`
                }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    required
                    label={t('Relationship to Student')}
                    placeholder={t('Select pickup contact')}
                  />
                )}
              />

              <TextField
                select
                fullWidth
                size="small"
                required
                label={t('Reason For Pickup')}
                value={purpose}
                onChange={event => setPurpose(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: value =>
                    value ? t(value) : t('Select reason'),
                }}
              >
                <MenuItem value="Student Pickup">
                  {t('Student Pickup')}
                </MenuItem>

                <MenuItem value="Early Dismissal">
                  {t('Early Dismissal')}
                </MenuItem>

                <MenuItem value="Other">
                  {t('Other')}
                </MenuItem>
              </TextField>

              {pickupIsNotPermitted && (
                <Alert severity="error">
                  {t(
                    'This person does not have permission to pick up the selected student.',
                  )}
                </Alert>
              )}
            </>
          )}

          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('Important Reminders')}
            </Typography>

            <Typography variant="caption" component="div">
              • {t('Check valid photo ID')}
            </Typography>

            {visitType === 'visit' && (
              <Typography variant="caption" component="div">
                • {t("Issue visitor pass with today's color")}
              </Typography>
            )}

            {visitType === 'pickup' && (
              <Typography variant="caption" component="div">
                • {t('Verify against authorized pickup list')}
              </Typography>
            )}

            <Typography variant="caption" component="div">
              • {t('Explain check-out procedure')}
            </Typography>
          </Alert>
        </Stack>
      </CardContent>

      <CardActions
        sx={{
          justifyContent: 'flex-end',
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(basePath)}
        >
          {t('Cancel')}
        </Button>

        <Button
          variant="contained"
          disabled={saveDisabled}
          onClick={handleSave}
        >
          {saving ? t('Saving...') : t('Check In Visitor')}
        </Button>
      </CardActions>
    </Card>
  )
}